<template>
  <Sheet :open="isOpen" @update:open="handleOpenChange">
    <SheetContent
      side="right"
      class="!w-[min(900px,96vw)] !max-w-none flex h-full flex-row gap-0 overflow-hidden p-0"
    >
      <SheetTitle class="sr-only">{{ $t("notifications.title") }}</SheetTitle>

      <NotificationsSidebar
        :active-key="activeCategory"
        :categories="categories"
        :total-unread="unread"
        :loading-categories="loadingCategories"
        @select="onSelectCategory"
      />

      <div ref="listRef" class="flex min-w-0 flex-1 flex-col">
        <header
          class="flex items-center justify-between gap-3 border-b border-border py-3 pl-6 pr-12"
        >
          <Tag variant="primary" size="sm" class="rounded-md">
            {{ $t("notifications.newBadge") }} · {{ unread }}
          </Tag>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="unread === 0 || markingAll"
            @click="onMarkAllRead"
          >
            <CheckCheck class="h-3.5 w-3.5" />
            {{ $t("notifications.markAllRead") }}
          </button>
        </header>

        <NotificationsList
          class="flex-1"
          :items="items"
          :loading="loading"
          :loading-more="loadingMore"
          :has-more="hasMore"
          :is-empty="isEmpty"
          :error="error"
          @mark-read="markRead"
          @load-more="loadMore"
          @retry="refresh"
        />
      </div>
    </SheetContent>
  </Sheet>
</template>

<script setup>
import { ref } from "vue";
import { Sheet, SheetContent, SheetTitle } from "@/shared/ui/sheet";
import { Tag } from "@/shared/ui/tag";
import { CheckCheck } from "lucide-vue-next";
import {
  NotificationsList,
  NotificationsSidebar,
  useNotificationsList,
} from "@/features/notifications-panel";
import { storeToRefs } from "pinia";
import { useNotificationStore } from "@/entities/notification";

const isOpen = ref(false);
const listRef = ref(null);

const {
  items,
  categories,
  unread,
  loading,
  loadingMore,
  markingAll,
  error,
  isEmpty,
  hasMore,
  activeCategory,
  refresh,
  loadMore,
  markRead,
  markAllRead,
  setFilters,
  fetchCategories,
  fetchCounter,
} = useNotificationsList();

const store = useNotificationStore();
const { loadingCategories } = storeToRefs(store);

async function open() {
  isOpen.value = true;
  await Promise.all([refresh(), fetchCategories(), fetchCounter()]);
}

function close() {
  isOpen.value = false;
}

function handleOpenChange(next) {
  if (next) open();
  else close();
}

function onSelectCategory(key) {
  setFilters({ categories: key && key !== "all" ? [key] : [] });
  scrollTop();
}

function scrollTop() {
  const el = listRef.value?.querySelector('[role="list"]');
  if (el) el.scrollTop = 0;
}

async function onMarkAllRead() {
  await markAllRead();
}

defineExpose({ open, close });
</script>
